import {
  applyOperation,
  normalizeRational,
  type AlgebraOperation,
  type Equation,
} from '../../shared/algebra.ts';

export type NarratedWord = {
  text: string;
  startMs: number;
  endMs: number;
};

export type NarratedOperation = {
  before: Equation;
  operation: AlgebraOperation;
  after: Equation;
  progress: number;
  startMs: number;
};

const numberWords: Record<string, string> = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5',
  six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
  eleven: '11', twelve: '12', thirteen: '13', fourteen: '14',
  fifteen: '15', sixteen: '16', seventeen: '17', eighteen: '18',
  nineteen: '19', twenty: '20',
};
const operationKinds: Record<string, AlgebraOperation['kind']> = {
  add: 'add', subtract: 'subtract', multiply: 'multiply', divide: 'divide',
};
const allowedPrefixes = new Set([
  '', 'first', 'now', 'next', 'then', 'so', 'please', 'lets',
  'first please', 'now please',
]);

const tokensFor = (text: string): string[] =>
  text.toLowerCase().replace(/\blet[’']s\b/g, 'lets').replace(/[−–—]/g, '-')
    .split(/\s+/)
    .map((token) => token.replace(/[.,!?;:]+$/, ''))
    .filter(Boolean);

const copyEquation = (equation: Equation): Equation => ({
  left: { ...equation.left },
  right: { ...equation.right },
});

const strictValue = (
  tokens: string[],
): { value: string; valueOffset: number } | undefined => {
  const negative = tokens.length === 2 && tokens[0] === 'negative';
  if ((!negative && tokens.length !== 1) || (negative && tokens.length !== 2))
    return undefined;
  const token = tokens[negative ? 1 : 0];
  const numeric = numberWords[token] ?? token;
  if (negative && numeric.startsWith('-')) return undefined;
  if (!/^-?\d+(?:\.\d+)?(?:\/\d+)?$/.test(numeric)) return undefined;
  try {
    return {
      value: normalizeRational(`${negative && !numeric.startsWith('-') ? '-' : ''}${numeric}`),
      valueOffset: negative ? 1 : 0,
    };
  } catch {
    return undefined;
  }
};

const exactDirective = (
  kind: AlgebraOperation['kind'],
  tokens: string[],
): { operation: AlgebraOperation; valueOffset: number } | undefined => {
  if (kind === 'add' || kind === 'subtract') {
    const preposition = kind === 'add' ? 'to' : 'from';
    for (const sideWords of [
      [preposition, 'both', 'sides'],
      [preposition, 'both', 'entire', 'sides'],
    ]) {
      if (tokens.length <= sideWords.length) continue;
      const split = tokens.length - sideWords.length;
      if (!sideWords.every((token, index) => tokens[split + index] === token))
        continue;
      const parsed = strictValue(tokens.slice(0, split));
      if (parsed)
        return {
          operation: { kind, value: parsed.value },
          valueOffset: parsed.valueOffset,
        };
    }
    return undefined;
  }
  for (const sideWords of [
    ['both', 'sides', 'by'],
    ['both', 'entire', 'sides', 'by'],
  ]) {
    if (!sideWords.every((token, index) => tokens[index] === token)) continue;
    const parsed = strictValue(tokens.slice(sideWords.length));
    if (parsed)
      return {
        operation: { kind, value: parsed.value },
        valueOffset: sideWords.length + parsed.valueOffset,
      };
  }
  return undefined;
};

const firstOperation = (
  equation: Equation,
  words: readonly NarratedWord[],
): { operation: AlgebraOperation; startMs: number; phraseEndMs: number } | undefined => {
  const flattened = words.flatMap((word, wordIndex) =>
    tokensFor(word.text).map((text) => ({ text, wordIndex })),
  );
  const allTokens = flattened.map((token) => token.text);
  for (let index = 0; index < flattened.length; index += 1) {
    const kind = operationKinds[flattened[index].text];
    if (!kind) continue;
    let sentenceStart = 0;
    for (let cursor = 0; cursor < index; cursor += 1) {
      const wordIndex = flattened[cursor].wordIndex;
      if (
        /[.!?]["']?$/.test(words[wordIndex].text) &&
        flattened[cursor + 1]?.wordIndex !== wordIndex
      )
        sentenceStart = cursor + 1;
    }
    let sentenceEnd = flattened.length;
    for (let cursor = index; cursor < flattened.length; cursor += 1) {
      const wordIndex = flattened[cursor].wordIndex;
      if (
        /[.!?]["']?$/.test(words[wordIndex].text) &&
        flattened[cursor + 1]?.wordIndex !== wordIndex
      ) {
        sentenceEnd = cursor + 1;
        break;
      }
    }
    const sentenceTokens = allTokens.slice(sentenceStart, sentenceEnd);
    const finalWord = words[flattened[sentenceEnd - 1].wordIndex].text;
    const operationOffset = index - sentenceStart;
    const prefix = sentenceTokens.slice(0, operationOffset).join(' ');
    if (!allowedPrefixes.has(prefix) || /\?["']?$/.test(finalWord)) continue;
    const parsed = exactDirective(kind, sentenceTokens.slice(operationOffset + 1));
    if (!parsed) continue;
    try {
      applyOperation(equation, parsed.operation);
      const valueToken = index + 1 + parsed.valueOffset;
      return {
        operation: parsed.operation,
        startMs: words[flattened[index].wordIndex].startMs,
        phraseEndMs: Math.max(
          words[flattened[valueToken].wordIndex].endMs,
          words[flattened[sentenceEnd - 1].wordIndex].endMs,
        ),
      };
    } catch {
      continue;
    }
  }
  return undefined;
};

/**
 * Derives one reversible, nonmutating preview from aligned spoken words.
 * The first explicit operation on both sides wins; later prose is never chained.
 */
export function deriveNarratedOperation(
  equation: Equation,
  words: readonly NarratedWord[],
  positionMs: number,
): NarratedOperation | undefined {
  if (
    !words.length ||
    !Number.isFinite(positionMs) ||
    words.some(
      (word) =>
        !Number.isFinite(word.startMs) ||
        !Number.isFinite(word.endMs) ||
        word.startMs < 0 ||
        word.endMs <= word.startMs,
    )
  )
    return undefined;
  const candidate = firstOperation(equation, words);
  if (!candidate || positionMs < candidate.startMs) return undefined;
  try {
    const before = copyEquation(equation);
    const after = applyOperation(before, candidate.operation);
    const endMs = Math.max(candidate.startMs + 1, candidate.phraseEndMs + 3_000);
    return {
      before,
      operation: candidate.operation,
      after,
      progress: Math.min(
        1,
        Math.max(0, (positionMs - candidate.startMs) / (endMs - candidate.startMs)),
      ),
      startMs: candidate.startMs,
    };
  } catch {
    return undefined;
  }
}

export const narratedOperation = deriveNarratedOperation;
